import React, { useState, useEffect } from "react";
import {
  UsersIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { adminService } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import toast from "react-hot-toast";

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.getUsers(filters);

      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      toast.error("Kullanıcılar yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (updateData) => {
    try {
      await adminService.updateUser(selectedUser.id, updateData);
      toast.success("Kullanıcı başarıyla güncellendi");
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error("Kullanıcı güncellenirken bir hata oluştu");
    }
  };

  const getRoleBadge = (role) => {
    return role === "admin" ? (
      <span className="badge badge-danger">Admin</span>
    ) : (
      <span className="badge badge-primary">User</span>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge badge-success">Aktif</span>
    ) : (
      <span className="badge badge-gray">Pasif</span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Kullanıcı Yönetimi
          </h1>
          <p className="text-gray-600">Sistem kullanıcılarını yönetin</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <p className="text-sm text-gray-500">
            Toplam: {pagination.totalItems || 0} kullanıcı
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="E-posta, ad veya soyad ara..."
                className="form-input pl-10"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>

            <select
              className="form-select"
              value={filters.role ?? ""}
              onChange={(e) => {
                const { value } = e.target;
                setFilters((prev) =>
                  value !== ""
                    ? { ...prev, role: value }
                    : Object.fromEntries(
                        Object.entries(prev).filter(([k]) => k !== "role")
                      )
                );
              }}
            >
              <option value="">Tüm Roller</option>
              <option value="user">Kullanıcı</option>
              <option value="admin">Admin</option>
            </select>

            <select
              className="form-select"
              value={filters.status ?? ""}
              onChange={(e) => {
                const { value } = e.target;
                setFilters((prev) =>
                  value !== ""
                    ? { ...prev, status: value }
                    : Object.fromEntries(
                        Object.entries(prev).filter(([k]) => k !== "status")
                      )
                );
              }}
            >
              <option value="">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>

            <button type="submit" className="btn btn-primary">
              Filtrele
            </button>
          </form>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" text="Kullanıcılar yükleniyor..." />
            </div>
          ) : users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-hover w-full">
                <thead>
                  <tr>
                    <th>Kullanıcı</th>
                    <th>Rol</th>
                    <th>Durum</th>
                    <th>Toplam Kazanç</th>
                    <th>Mevcut Bakiye</th>
                    <th>Kayıt Tarihi</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 font-medium">
                              {user.firstName?.[0]?.toUpperCase()}
                              {user.lastName?.[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.isActive)}</td>
                      <td className="font-medium">
                        ${user.totalEarnings?.toFixed(2)}
                      </td>
                      <td className="font-medium text-green-600">
                        ${user.availableBalance?.toFixed(2)}
                      </td>
                      <td>
                        {new Date(user.createdAt).toLocaleDateString("tr-TR")}
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="btn btn-secondary btn-sm"
                            title="Düzenle"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Kullanıcı bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Sayfa {pagination.currentPage} / {pagination.totalPages}(
                {pagination.totalItems} toplam kullanıcı)
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="btn btn-secondary btn-sm"
                >
                  Önceki
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }).map(
                  (_, index) => {
                    const pageNum =
                      Math.max(1, pagination.currentPage - 2) + index;
                    if (pageNum > pagination.totalPages) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm rounded ${
                          pageNum === pagination.currentPage
                            ? "bg-primary-100 text-primary-700"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="btn btn-secondary btn-sm"
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Kullanıcı Düzenle: {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </h3>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleUpdateUser({
                    role: formData.get("role"),
                    isActive: formData.get("isActive") === "true",
                    totalEarnings:
                      parseFloat(formData.get("totalEarnings")) || 0,
                    availableBalance:
                      parseFloat(formData.get("availableBalance")) || 0,
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol
                  </label>
                  <select
                    name="role"
                    defaultValue={selectedUser.role}
                    className="form-select"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    name="isActive"
                    defaultValue={selectedUser.isActive}
                    className="form-select"
                  >
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Toplam Kazanç
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="totalEarnings"
                    defaultValue={selectedUser.totalEarnings}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mevcut Bakiye
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="availableBalance"
                    defaultValue={selectedUser.availableBalance}
                    className="form-input"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedUser(null);
                    }}
                    className="btn btn-secondary"
                  >
                    İptal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManagement;
